import { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Divider, Button, TitleBold6, DateTools } from '@treezor/design-system';

import { InformationBlock } from '@/features/cards/components/CardUI/InformationBlock';
import { IPaymentCard } from '@/features/cards/store/models/card.model';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RenewalStatus = styled.label`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.theme.text.secondary};
  background-color: ${({ theme }) => theme.theme.tag.gray.background};
  font-weight: ${({ theme }) => theme.typography?.fontWeightBold};
  border-radius: ${({ theme }) => theme.radius[100]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: 12px;
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0.2rem 0;
`;

type TRenewalBlockProps = {
  paymentCard: IPaymentCard;
  onClose: () => void;
};

export function RenewalBlock({ paymentCard, onClose }: TRenewalBlockProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onSeeOriginalCard = useCallback(() => {
    onClose();
    navigate(DashboardHelpers.generateProfilePath(paymentCard.userId, { cardId: paymentCard.originalCardId }));
  }, [navigate, paymentCard, onClose]);

  return (
    <Container>
      <HeaderContainer>
        <TitleBold6> {t(`pages.currentProfiles.cards.card.renewal.title`)}</TitleBold6>
        {!!paymentCard.renewalType && (
          <RenewalStatus>
            {t(
              `pages.currentProfiles.cards.card.renewal.${paymentCard.renewalType === 'N' ? 'noRenewal' : 'automatic'}`,
            )}
          </RenewalStatus>
        )}
      </HeaderContainer>
      <Content>
        <InformationBlock
          title={t('pages.currentProfiles.cards.card.renewal.expirationDate')}
          value={DateTools.format(paymentCard.expiryDate) || '-'}
        />
        {!!paymentCard.renewalDate && (
          <InformationBlock
            title={t('pages.currentProfiles.cards.card.renewal.renewalDate')}
            value={DateTools.format(paymentCard.renewalDate) || '-'}
          />
        )}
      </Content>
      {!!paymentCard.originalCardId && (
        <>
          <Divider />
          <ButtonContainer>
            <Button icon="arrow_forward" iconPosition="right" variant="ghost" onClick={onSeeOriginalCard}>
              {t('pages.currentProfiles.cards.card.renewal.seeOriginalCard')}
            </Button>
          </ButtonContainer>
        </>
      )}
    </Container>
  );
}
